// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { removeTab } from 'wdna-components/lib/tabs';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { sensorParameterTypes, TypeSensorParameterDO } from '../../../requests';

import Form from './Form';
import ButtonsForm from '../ButtonsForm';

interface Props {
  rowId?: number;
  notify: () => void;
  typeSensor: any;
}

interface State {
  loading: boolean;
  data: Partial<TypeSensorParameterDO>;
}

export default class Edit extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private defaultValues: any;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.defaultValues = {
      typeSensor: this.props.typeSensor,
    };

    this.state = {
      loading: false,
      data: {}
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  };

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    const { rowId } = this.props || {};
    if (rowId && !isNaN(rowId)) {
      this.loadingStart();
      sensorParameterTypes<TypeSensorParameterDO>('get', String(rowId))
        ?.then((resp: TypeSensorParameterDO) => {
          this.setState({
            data: {
              ...resp,
            }
          }, () => {
            this.defaultValues = _.cloneDeep(resp);
          });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    }
  };

  public loadingStart = (): void => {
    this.setState({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setState({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    this.setState({
      data: {
        ...this.defaultValues,
      }
    });
  };

  public submit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const { data } = this.state;

    this.loadingStart();
    sensorParameterTypes<TypeSensorParameterDO>('set', undefined, data)
      ?.then(() => {
        messageInfo('Success', `TypeSensorParameter successfully ${data.id ? 'edited' : 'created'}`, 5000);
        const { notify } = this.props || {};
        if (typeof notify === 'function') {
          notify();
        }
        window.store.dispatch(removeTab('VE'));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, checked, type, dataset } = e.target;
    const { handlerType } = dataset || {};
    if (type === 'checkbox') {
      if (handlerType === 'integer') {
        this.setState((prevState: State) => ({
          data: {
            ...prevState.data,
            [name]: checked ? 1 : 0,
          },
        }));
      } else {
        this.setState((prevState: State) => ({
          data: {
            ...prevState.data,
            [name]: !!checked,
          },
        }));
      }
    } else if (name.indexOf('.') > -1) {
      const [outerName, innerName] = name.split('.');
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [outerName]: {
            [innerName]: value,
          },
        },
      }));
    } else {
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [name]: value,
        },
      }));
    }
  };

  public render(): JSX.Element {
    const { loading, data } = this.state;

    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={loading} overlay={false} />
                  <UiValidate>
                    <form className="smart-form" onSubmit={this.submit} method="post" encType="multipart/form-data">
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Parameter (Sensor-Type) {data.id ? `Edit: ${data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form formValues={this.state} onChange={this.handleChange} />
                      <ButtonsForm loading={loading} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
